/**
 * Parse delimited text into rows of cells. Handles RFC-4180-style quoting:
 * fields wrapped in double quotes may contain the delimiter, newlines, and
 * escaped quotes (`""`). Not a full CSV implementation — good enough for
 * previewing well-formed CSV/TSV files.
 */
function parseDelimited(content: string, delimiter: string): string[][] {
  const rows: string[][] = [];
  let field = '';
  let row: string[] = [];
  let inQuotes = false;
  let sawAny = false;

  const pushField = () => {
    row.push(field);
    field = '';
  };
  const pushRow = () => {
    pushField();
    rows.push(row);
    row = [];
  };

  for (let i = 0; i < content.length; i++) {
    const char = content[i];
    sawAny = true;

    if (inQuotes) {
      if (char === '"') {
        if (content[i + 1] === '"') {
          field += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        field += char;
      }
      continue;
    }

    if (char === '"') {
      inQuotes = true;
    } else if (char === delimiter) {
      pushField();
    } else if (char === '\n') {
      pushRow();
    } else if (char === '\r') {
      // swallow — handled by the following \n (or end of input)
    } else {
      field += char;
    }
  }

  // Flush the trailing field/row unless the input ended exactly on a newline.
  if (field.length > 0 || row.length > 0 || (sawAny && rows.length === 0)) {
    pushRow();
  }

  return rows;
}

export function TableView({ content, delimiter }: { content: string; delimiter?: string }) {
  const rows = parseDelimited(content, delimiter ?? ',');

  if (rows.length === 0) {
    return (
      <div className="p-4 text-body text-muted-foreground">No rows to display.</div>
    );
  }

  const [header, ...body] = rows;

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full border-collapse text-caption">
        <thead>
          <tr>
            {header.map((cell, i) => (
              <th
                key={i}
                className="border-b border-border px-2 py-1 text-left font-medium"
              >
                {cell}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {body.map((cells, r) => (
            <tr key={r}>
              {cells.map((cell, c) => (
                <td key={c} className="border-b border-border/40 px-2 py-1">
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
